/**
 * Reparto entre asociados — CRM-side helpers for the editor table and the
 * retroactive dialog. The money math (resolveReparto / rescaleReparto) lives in
 * calculations.js, mirrored in backend-nyx comision_service.py; this file only
 * shapes UI rows and diffs what is booked against what would be booked.
 */
import {
  parseNumber, resolveReparto, getFechaEmision, getFiado, getEmisorNombre, ASOCIADO_TIPO_SOCIO,
} from './calculations';

const round2 = (x) => Math.round(x * 100) / 100 + 0;

/**
 * One editor row per active asociado (prefilled, never pick-then-add), plus
 * any inactive one the stored split still names so the owner sees it. Socios
 * first, then asociados, each by name.
 */
export function rowsParaEditor(asociados, valor) {
  const pcts = {};
  (valor || []).forEach((r) => { if (r?.asociadoId) pcts[r.asociadoId] = r.pct; });
  return (asociados || [])
    .filter((a) => a.activo !== false || pcts[a.id] != null)
    .slice()
    .sort((a, b) => {
      const ta = a.tipo === ASOCIADO_TIPO_SOCIO ? 0 : 1;
      const tb = b.tipo === ASOCIADO_TIPO_SOCIO ? 0 : 1;
      return ta - tb || (a.nombre || '').localeCompare(b.nombre || '', 'es');
    })
    .map((a) => ({
      asociadoId: a.id,
      nombre: a.nombre,
      tipo: a.tipo === ASOCIADO_TIPO_SOCIO ? ASOCIADO_TIPO_SOCIO : 'asociado',
      activo: a.activo !== false,
      pct: pcts[a.id] != null ? String(pcts[a.id]) : '',
    }));
}

export const sumaRows = (rows) =>
  round2((rows || []).reduce((s, r) => s + (parseNumber(r.pct) || 0), 0));

/**
 * The stored primaNeta for editor rows: asociados with a pct > 0, and every
 * socio (a socio at 0% stays — it is where an inactive asociado's share lands).
 * Null when nobody has a pct (no reparto for this cliente).
 */
export function primaNetaDesdeRows(rows) {
  const out = (rows || [])
    .map((r) => ({ asociadoId: r.asociadoId, tipo: r.tipo, pct: parseNumber(r.pct) || 0 }))
    .filter((r) => r.pct > 0 || r.tipo === ASOCIADO_TIPO_SOCIO);
  if (!out.some((r) => r.pct > 0)) return null;
  return out.map(({ asociadoId, pct }) => ({ asociadoId, pct }));
}

/**
 * Default split of the non-prima income (bonos) — socios only, renormalized
 * from their prima-neta pcts. Stored now, rendered once bonos exist.
 */
export function otrosIngresosDesde(primaNeta, asociadosById) {
  const socios = (primaNeta || []).filter(
    (r) => asociadosById?.[r.asociadoId]?.tipo === ASOCIADO_TIPO_SOCIO && r.pct > 0
  );
  const total = socios.reduce((s, r) => s + r.pct, 0);
  if (!total) return null;
  const out = socios.map((r) => ({ asociadoId: r.asociadoId, pct: round2((r.pct * 100) / total) }));
  const residuo = round2(100 - out.reduce((s, r) => s + r.pct, 0));
  if (residuo) out[0].pct = round2(out[0].pct + residuo);
  return out;
}

/** Validation message for editor rows, or null when they can be saved. */
export function errorRows(rows) {
  for (const r of rows || []) {
    if (r.pct === '' || r.pct == null) continue;
    const n = parseNumber(r.pct);
    if (n == null || n < 0 || n > 100) return 'Cada porcentaje debe estar entre 0 y 100.';
  }
  const suma = sumaRows(rows);
  if (suma === 0) return null;
  if (Math.abs(suma - 100) > 0.01) return `El reparto suma ${suma}%; debe sumar 100%.`;
  return null;
}

/** asociadoId → pct of the first non-reversal snapshot (what is booked). */
const pctsDeMovs = (movs) => {
  const m = movs.find((x) => !x.reversaDeMovId && Array.isArray(x.reparto) && x.reparto.length)
    || movs.find((x) => Array.isArray(x.reparto) && x.reparto.length);
  if (!m) return null;
  return m.reparto;
};

const mismaDistribucion = (a, b) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  const ma = Object.fromEntries(a.map((r) => [r.asociadoId, round2(r.pct)]));
  const mb = Object.fromEntries(b.map((r) => [r.asociadoId, round2(r.pct)]));
  const keys = new Set([...Object.keys(ma), ...Object.keys(mb)]);
  for (const k of keys) if ((ma[k] || 0) !== (mb[k] || 0)) return false;
  return true;
};

/**
 * Fianzas on the books whose booked reparto differs from the one that would
 * resolve today — the rows of RepartoRetroactivoDialog.
 *
 * @param {Object} p
 * @param {Object[]} p.movements      the org ledger (DataContext)
 * @param {Object[]} p.contratos      contracts to consider (e.g. the cliente's)
 * @param {Object}   p.clientesById   clienteId → cliente doc
 * @param {Object}   p.asociadosById
 * @param {Object}   [p.overrides]    fianzaId → fianza to use instead of the
 *                                    stored one (a just-edited override)
 * @returns [{contractId, fianzaId, clienteId, numeroFianza, fecha, fiadoNombre,
 *            afianzadora, comision, actual, nuevo,
 *            diferencias: [{asociadoId, nombre, antes, despues}]}]
 *          newest emisión first (fecha = getFechaEmision, fianzas without one last).
 */
export function candidatosReparto({ movements, contratos, clientesById, asociadosById, overrides = {} }) {
  const porFianza = {};
  (movements || []).forEach((m) => {
    if (!m.fianzaId) return;
    (porFianza[m.fianzaId] = porFianza[m.fianzaId] || []).push(m);
  });
  const out = [];
  (contratos || []).forEach((c) => {
    const cliente = clientesById?.[c.clientId] || {};
    (c.fianzas || []).forEach((f0) => {
      const movs = (porFianza[f0.id] || []).filter((m) => !m.contratoId || m.contratoId === c.id);
      if (!movs.length) return;
      const f = overrides[f0.id] || f0;
      const comision = round2(movs.reduce((s, m) => s + (Number(m.comisionMonto) || 0), 0));
      const actual = pctsDeMovs(movs);
      const nuevo = resolveReparto(cliente, f, asociadosById, comision);
      if (mismaDistribucion(actual, nuevo)) return;
      const antesPor = {};
      movs.forEach((m) => (m.reparto || []).forEach((r) => {
        antesPor[r.asociadoId] = round2((antesPor[r.asociadoId] || 0) + (Number(r.monto) || 0));
      }));
      const despuesPor = Object.fromEntries((nuevo || []).map((r) => [r.asociadoId, r.monto]));
      const ids = [...new Set([...Object.keys(antesPor), ...Object.keys(despuesPor)])];
      out.push({
        contractId: c.id,
        fianzaId: f0.id,
        clienteId: c.clientId || null,
        numeroFianza: f0.numeroFianza || movs[0]?.numeroFianza || null,
        clienteNombre: cliente.nombreEmpresa || movs[0]?.clienteNombre || null,
        fecha: getFechaEmision(f0) || Number(movs[0]?.fecha) || null,
        fiadoNombre: getFiado(f0).nombre || movs[0]?.fiadoNombre || null,
        afianzadora: getEmisorNombre(f0) || movs[0]?.afianzadora || null,
        comision,
        actual,
        nuevo,
        diferencias: ids.map((id) => ({
          asociadoId: id,
          nombre: asociadosById?.[id]?.nombre
            || (actual || []).find((r) => r.asociadoId === id)?.nombre || id,
          antes: antesPor[id] || 0,
          despues: despuesPor[id] || 0,
        })),
      });
    });
  });
  return out.sort((a, b) => (b.fecha || 0) - (a.fecha || 0));
}

/** "2026-10-01" (local) → ms at local midnight, or null for an empty/invalid value. */
const inicioDeDiaISO = (iso) => {
  if (!iso) return null;
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).getTime();
};

/**
 * The candidatos emitted on or after an ISO date (the dialog's "Desde"
 * filter): the asociado joined from a given month on, earlier fianzas are
 * left alone. Fianzas without a fecha are kept so they are never hidden by
 * accident. No date → every candidato.
 */
export function candidatosDesde(candidatos, desdeISO) {
  const desde = inicioDeDiaISO(desdeISO);
  if (desde == null) return candidatos || [];
  return (candidatos || []).filter((c) => !c.fecha || c.fecha >= desde);
}

/** "Socio A 40%, Socio B 40%, Despacho 1 20%" — for bitácora descriptions. */
export function describirReparto(primaNeta, asociadosById) {
  if (!primaNeta || !primaNeta.length) return 'sin reparto';
  return primaNeta
    .filter((r) => r.pct > 0)
    .map((r) => `${asociadosById?.[r.asociadoId]?.nombre || r.asociadoId} ${r.pct}%`)
    .join(', ');
}

/** bitácora changes[] for a reparto edit, keyed reparto.<asociadoId>. */
export function cambiosReparto(antes, despues) {
  const a = Object.fromEntries((antes || []).map((r) => [r.asociadoId, r.pct]));
  const d = Object.fromEntries((despues || []).map((r) => [r.asociadoId, r.pct]));
  const ids = [...new Set([...Object.keys(a), ...Object.keys(d)])];
  return ids
    .filter((id) => (a[id] ?? null) !== (d[id] ?? null))
    .map((id) => ({
      field: `reparto.${id}`,
      oldValue: a[id] == null ? null : `${a[id]}%`,
      newValue: d[id] == null ? null : `${d[id]}%`,
    }));
}

/** "2026-10" for a year and 0-based month. */
export const periodoDe = (anio, mes) => `${anio}-${String(mes + 1).padStart(2, '0')}`;

/**
 * Comisión por asociado for one calendar month (by movement fecha, local
 * time — the same month the backend freezes when a pago is marked).
 *
 * Every movement lands somewhere: its reparto[] cuts go to their asociados and
 * a movement booked without reparto goes to `sinReparto`, so `total` equals
 * the month's net comisión in Liquidación.
 *
 * @returns {{ rows: [{asociadoId, nombre, tipo, monto, fianzas, movimientos: Object[]}],
 *             sinReparto: {monto, movimientos: Object[]}, total: number }}
 */
export function comisionPorAsociado(movements, anio, mes, asociadosById = {}) {
  const map = new Map();
  const sinReparto = { monto: 0, movimientos: [] };
  let total = 0;
  (movements || []).forEach((m) => {
    const ms = Number(m.fecha) || 0;
    if (!ms) return;
    const d = new Date(ms);
    if (d.getFullYear() !== anio || d.getMonth() !== mes) return;
    const monto = Number(m.comisionMonto) || 0;
    total += monto;
    if (!Array.isArray(m.reparto) || m.reparto.length === 0) {
      sinReparto.monto += monto;
      sinReparto.movimientos.push(m);
      return;
    }
    m.reparto.forEach((r) => {
      if (!map.has(r.asociadoId)) {
        const a = asociadosById[r.asociadoId];
        map.set(r.asociadoId, {
          asociadoId: r.asociadoId,
          nombre: a?.nombre || r.nombre || r.asociadoId,
          tipo: a?.tipo || r.tipo,
          monto: 0,
          fianzas: new Set(),
          movimientos: [],
        });
      }
      const g = map.get(r.asociadoId);
      g.monto += Number(r.monto) || 0;
      if (m.fianzaId) g.fianzas.add(m.fianzaId);
      g.movimientos.push({ ...m, parte: Number(r.monto) || 0, partePct: r.pct });
    });
  });
  const rows = Array.from(map.values())
    .map((g) => ({ ...g, monto: round2(g.monto), fianzas: g.fianzas.size }))
    .sort((a, b) => b.monto - a.monto);
  sinReparto.monto = round2(sinReparto.monto);
  return { rows, sinReparto, total: round2(total) };
}

/**
 * The year, month by month, for the annual view of the report: one row per
 * asociado with twelve montos and a total, the "Sin reparto" row, and the
 * month totals (each equals that month's net comisión). Built from
 * comisionPorAsociado so both views square the same way.
 *
 * @returns {{ rows: [{asociadoId, nombre, tipo, meses: number[12], total}],
 *             sinReparto: {meses: number[12], total}, totalesMes: number[12], total }}
 */
export function comisionPorAsociadoAnual(movements, anio, asociadosById = {}) {
  const map = new Map();
  const sinReparto = { meses: Array(12).fill(0), total: 0 };
  const totalesMes = Array(12).fill(0);
  for (let mes = 0; mes < 12; mes += 1) {
    const m = comisionPorAsociado(movements, anio, mes, asociadosById);
    totalesMes[mes] = m.total;
    sinReparto.meses[mes] = m.sinReparto.monto;
    sinReparto.total = round2(sinReparto.total + m.sinReparto.monto);
    m.rows.forEach((r) => {
      if (!map.has(r.asociadoId)) {
        map.set(r.asociadoId, {
          asociadoId: r.asociadoId, nombre: r.nombre, tipo: r.tipo, meses: Array(12).fill(0), total: 0,
        });
      }
      const g = map.get(r.asociadoId);
      g.meses[mes] = r.monto;
      g.total = round2(g.total + r.monto);
    });
  }
  const rows = Array.from(map.values()).sort((a, b) => {
    const ta = a.tipo === ASOCIADO_TIPO_SOCIO ? 0 : 1;
    const tb = b.tipo === ASOCIADO_TIPO_SOCIO ? 0 : 1;
    return ta - tb || b.total - a.total;
  });
  return { rows, sinReparto, totalesMes, total: round2(totalesMes.reduce((s, x) => s + x, 0)) };
}

/**
 * A fianza's reparto as booked: per asociado, the net of its cuts over every
 * movement (reversals included) and the pct of the latest non-reversal
 * snapshot. Socios first. Empty when no movement carries a reparto.
 */
export function repartoDeFianza(movements, asociadosById = {}) {
  const map = new Map();
  const porFecha = (movements || []).slice().sort((a, b) => (Number(a.fecha) || 0) - (Number(b.fecha) || 0));
  porFecha.forEach((m) => {
    (Array.isArray(m.reparto) ? m.reparto : []).forEach((r) => {
      if (!map.has(r.asociadoId)) {
        map.set(r.asociadoId, { asociadoId: r.asociadoId, nombre: r.nombre, tipo: r.tipo, pct: r.pct, monto: 0 });
      }
      const g = map.get(r.asociadoId);
      g.monto += Number(r.monto) || 0;
      if (!m.reversaDeMovId) g.pct = r.pct;
    });
  });
  return Array.from(map.values())
    .map((g) => {
      const a = asociadosById[g.asociadoId];
      return { ...g, nombre: a?.nombre || g.nombre || g.asociadoId, tipo: a?.tipo || g.tipo, monto: round2(g.monto) };
    })
    .sort((a, b) => {
      const ta = a.tipo === ASOCIADO_TIPO_SOCIO ? 0 : 1;
      const tb = b.tipo === ASOCIADO_TIPO_SOCIO ? 0 : 1;
      return ta - tb || b.monto - a.monto;
    });
}
